import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminRequestsService } from '../services/admin.requests.service'
import type { ServiceRequestDocument, RequestStatus } from '@/types'

interface UseAdminRequestsResult {
  requests: Array<ServiceRequestDocument & { id: string }>
  loading: boolean
  error: string | null
  updateStatus: (id: string, status: RequestStatus) => Promise<void>
  refresh: () => void
}

export function useAdminRequests(): UseAdminRequestsResult {
  const [requests, setRequests] = useState<Array<ServiceRequestDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminRequestsService
      .getAllRequests()
      .then(setRequests)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load requests'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const updateStatus = useCallback(
    async (id: string, status: RequestStatus) => {
      await adminRequestsService.updateStatus(id, status)
      refresh()
    },
    [refresh]
  )

  return { requests, loading, error, updateStatus, refresh }
}
